export enum MeetingType {
  INSTANT = 1,
  LATER = 2,
  SCHEDULE = 3
}

export enum MeetingStatus {
  CREATED = 1, // Meeting created but not started
  ONGOING = 2, // Meeting active (host or participants present)
  COMPLETED = 3, // Meeting officially ended by host
  CANCELED = 4, // Meeting was cancelled
  HOST_LEFT = 5, // Host left but participants still in meeting
  ABANDONED = 6 // All participants left (no one in meeting, not officially ended)
}

export enum RecordingStatus {
  STARTED = 1, // Recording is active
  STOPPED = 2, // Recording stopped successfully
  FAILED = 3, // Recording failed
  ENDING = 4 // Recording is being processed
}

export enum HostLeaveOption {
  END_FOR_ALL = 1,
  LEAVE_ONLY = 2
}

export enum Method {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE"
}

/* Status and type labels*/
export const STATUS_LABELS: Record<number, string> = {
  [MeetingStatus.CREATED]: "Created",
  [MeetingStatus.ONGOING]: "Ongoing",
  [MeetingStatus.COMPLETED]: "Completed",
  [MeetingStatus.CANCELED]: "Cancelled",
  [MeetingStatus.HOST_LEFT]: "Host Left",
  [MeetingStatus.ABANDONED]: "Abandoned"
};

export const TYPE_LABELS: Record<number, string> = {
  [MeetingType.INSTANT]: "Instant",
  [MeetingType.LATER]: "Later",
  [MeetingType.SCHEDULE]: "Scheduled"
};

/* User List Key Constants */
export enum UserListKey {
  INVITED_USERS = "invited",
  PENDING_USERS = "pending",
  APPROVED_USERS = "approved",
  REJECTED_USERS = "rejected",
  ALL_PARTICIPANTS = "allParticipants",
  JOINED_USERS = "joined",
  LEFT_USERS = "left",
  NOT_JOINED_USERS = "notJoined"
}

/** Track Type Enum Values */
export const TrackTypeEnum = {
  AUDIO: "audio",
  VIDEO: "video"
} as const;

export type TrackType = (typeof TrackTypeEnum)[keyof typeof TrackTypeEnum];

/** Default Track Type */
export const DEFAULT_TRACK_TYPE: TrackType = TrackTypeEnum.AUDIO;

/** Poll Status Enum Values */
export enum PollStatus {
  CREATED = 1,
  ENDED = 2
}

/**
 * Sort Order Enum
 * Used for database query sorting
 */
export enum SortOrder {
  ASCENDING = "asc",
  DESCENDING = "desc"
}

/**
 * Participant Action Enum
 * Used for adding/removing participants in API params
 */
export enum ParticipantAction {
  ADD = "add",
  REMOVE = "remove"
}

export enum ChatType {
  TEXT = 1, // Text message
  FILE = 2 // File message
}

/**
 * Local Storage Keys
 * Centralized keys for localStorage access
 */
export const STORAGE_KEYS = {
  ADMIN: {
    TOKEN: "adminToken",
    IS_AUTHENTICATED: "isAuthenticated",
    EMAIL: "adminEmail",
    NAME: "adminName",
    LOGIN_TIME: "loginTime"
  },
  CLIENT: {
    GUEST_MEETING_DATA: "guestMeetingData",
    GUEST_REJECTION_DATA: "guestRejectionData",
    MEDIA_PREFERENCES: "meetingMediaPreferences",
    HOST_ROOMS: "hostRooms"
  }
} as const;

/**
 * Time Constants
 * Common time values for unified management
 */
export const TIME_CONSTANTS = {
  // Delays (ms)
  LOGIN_DELAY: 1000,

  // Expiry
  GUEST_DATA_EXPIRY_DAYS: 15,
  GUEST_REJECTION_EXPIRY_HOURS: 24,

  // Unit Conversions
  SECONDS_PER_HOUR: 3600,
  SECONDS_PER_DAY: 86400,

  // PiP Controls
  PIP_AUTO_CLOSE_TIMEOUT: 5000
} as const;

/**
 * Email Recipient Type
 * Used for email notification preferences
 */
export enum EmailRecipientType {
  DO_NOT_SEND = 0, // Don't send email
  HOST_ONLY = 1, // Send to host only
  HOST_AND_PARTICIPANTS = 2 // Send to host and all participants
}

export const EMAIL_RECIPIENT_LABELS: Record<number, string> = {
  [EmailRecipientType.DO_NOT_SEND]: "Don't send email",
  [EmailRecipientType.HOST_ONLY]: "Host only",
  [EmailRecipientType.HOST_AND_PARTICIPANTS]: "Host & Participants"
};
